
import {
  collection, doc, addDoc, deleteDoc, onSnapshot,
  query, where, serverTimestamp, orderBy
} from 'firebase/firestore';
import { db } from './firebaseService';
import { SpotCondition, ConditionTag } from '../types';
import i18n from '../i18n';

const COLLECTION = 'spot_conditions';
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// -------------------------------------------------------
// Condition tag definitions
// -------------------------------------------------------

export const CONDITION_TAGS: {
  id: ConditionTag;
  emoji: string;
  label: string;
  labelKey: string;
  color: string; // tailwind bg class
  textColor: string;
}[] = [
  { id: 'godt_tilgjengelig',    emoji: '✅', label: 'Godt tilgjengelig',   labelKey: 'spot.conditions.godt_tilgjengelig',   color: 'bg-emerald-100', textColor: 'text-emerald-700' },
  { id: 'stille_og_rolig',      emoji: '😌', label: 'Stille og rolig',      labelKey: 'spot.conditions.stille_og_rolig',     color: 'bg-blue-100',    textColor: 'text-blue-700' },
  { id: 'bom_stengt',           emoji: '🚧', label: 'Bom/vei stengt',       labelKey: 'spot.conditions.bom_stengt',          color: 'bg-orange-100',  textColor: 'text-orange-700' },
  { id: 'vanskelig_adkomst',    emoji: '⚠️', label: 'Vanskelig adkomst',    labelKey: 'spot.conditions.vanskelig_adkomst',   color: 'bg-amber-100',   textColor: 'text-amber-700' },
  { id: 'mye_soppel',           emoji: '🗑️', label: 'Mye søppel',           labelKey: 'spot.conditions.mye_soppel',          color: 'bg-yellow-100',  textColor: 'text-yellow-700' },
  { id: 'oversvommt',           emoji: '🌊', label: 'Oversvømt',            labelKey: 'spot.conditions.oversvommt',          color: 'bg-sky-100',     textColor: 'text-sky-700' },
  { id: 'trenger_vedlikehold',  emoji: '🔧', label: 'Trenger vedlikehold',  labelKey: 'spot.conditions.trenger_vedlikehold', color: 'bg-stone-100',   textColor: 'text-stone-600' },
];

export function getConditionMeta(tag: string) {
  return CONDITION_TAGS.find(c => c.id === tag) ?? null;
}

// -------------------------------------------------------
// Read
// -------------------------------------------------------

/**
 * Real-time subscription to active (non-expired) conditions for a spot.
 * Returns unsubscribe function.
 */
export function subscribeToConditions(
  spotId: string,
  callback: (conditions: SpotCondition[]) => void
): () => void {
  const q = query(
    collection(db, COLLECTION),
    where('spotId', '==', spotId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    const now = Date.now();
    const conditions: SpotCondition[] = snap.docs
      .map(d => ({ id: d.id, ...(d.data() as Omit<SpotCondition, 'id'>) }))
      .filter(c => now - c.createdAt < EXPIRY_MS); // client-side expiry filter
    callback(conditions);
  }, () => callback([]));
}

// -------------------------------------------------------
// Write
// -------------------------------------------------------

export async function addConditionReport(
  spotId: string,
  userId: string,
  userName: string,
  tag: ConditionTag
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    spotId,
    userId,
    userName,
    tag,
    createdAt: Date.now(),
    _serverTs: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteConditionReport(conditionId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, conditionId));
}

// -------------------------------------------------------
// Utility
// -------------------------------------------------------

/** Formats relative time (e.g. "2h ago" / "Nettopp") */
export function conditionAge(createdAt: number): string {
  const diffMs = Date.now() - createdAt;
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffH < 1) return i18n.t('spot.conditions.age_just_now');
  if (diffH < 24) return i18n.t('spot.conditions.age_hours', { count: diffH });
  const diffD = Math.floor(diffH / 24);
  return i18n.t('spot.conditions.age_days', { count: diffD });
}
